import { ApiResponse } from '../types';
import { apiClient } from './apiClient';

export interface DashboardStats {
  totalSales: number;
  totalCustomers: number;
  totalProducts: number;
  pendingDues: number;
  todaySales: number;
  lowStockItems: number;
  monthlyProfit: number;
  todaysTransactions: number;
  periodTransactions: number;
  weeklyGrowth: number;
  monthlyGrowth: number;
  profitGrowth: number;
}

export interface SalesChart {
  period: string;
  sales: number;
  profit: number;
}

export interface TopSelling {
  product_name: string;
  quantity_sold: number;
  revenue: number;
}

export interface RecentSale {
  id: number;
  customer_name: string;
  total_amount: number;
  payment_status: 'paid' | 'partial' | 'due';
  sale_date: string;
  items_count: number;
}

export interface LowStockProduct {
  id: number;
  name: string;
  current_stock: number;
  minimum_stock_level: number;
  brand: string;
}

export interface SubscriptionInfo {
  subscription_status: 'active' | 'trial' | 'suspended' | 'expired';
  plan_type: 'basic' | 'standard' | 'premium' | 'enterprise';
  billing_cycle: 'monthly' | 'yearly';
  subscription_starts_at: string | null;
  subscription_ends_at: string | null;
  trial_ends_at: string | null;
  next_renewal_date: string | null;
  days_remaining: number | null;
  payment_status: 'paid' | 'pending' | 'overdue';
  monthly_fee: number | null;
  is_trial: boolean;
}

export interface DashboardData {
  stats: DashboardStats;
  salesChart: SalesChart[];
  topSelling: TopSelling[];
  recentSales: RecentSale[];
  lowStockProducts: LowStockProduct[];
  subscription?: SubscriptionInfo;
}

class DashboardService {
  private baseUrl = '/dashboard';

  async getDashboardData(period: 'today' | 'week' | 'month' = 'today'): Promise<DashboardData> {
    try {
      const response = await apiClient.get<ApiResponse<DashboardData>>(
        `${this.baseUrl}/summary?period=${period}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch dashboard data');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw new Error('Failed to fetch dashboard data. Please check your connection and ensure the backend API is running.');
    }
  }

  async getQuickStats(): Promise<DashboardStats> {
    try {
      const response = await apiClient.get<ApiResponse<DashboardStats>>(
        `${this.baseUrl}/stats`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch stats');
    } catch (error) {
      console.error('Error fetching quick stats:', error);
      throw new Error('Failed to fetch statistics. Please check your connection and ensure the backend API is running.');
    }
  }

  async getSalesChart(period: '7days' | '30days' | '90days' = '7days'): Promise<SalesChart[]> {
    try {
      const response = await apiClient.get<ApiResponse<SalesChart[]>>(
        `${this.baseUrl}/chart?period=${period}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch sales chart');
    } catch (error) {
      console.error('Error fetching sales chart:', error);
      throw new Error('Failed to fetch sales chart. Please check your connection and ensure the backend API is running.');
    }
  }

  async getTopSellingProducts(limit: number = 8): Promise<TopSelling[]> {
    try {
      const response = await apiClient.get<ApiResponse<TopSelling[]>>(
        `${this.baseUrl}/top-selling?limit=${limit}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch top selling products');
    } catch (error) {
      console.error('Error fetching top selling products:', error);
      throw new Error('Failed to fetch top selling products. Please check your connection and ensure the backend API is running.');
    }
  }

  async getRecentSales(limit: number = 8): Promise<RecentSale[]> {
    try {
      const response = await apiClient.get<ApiResponse<RecentSale[]>>(
        `${this.baseUrl}/recent-sales?limit=${limit}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch recent sales');
    } catch (error) {
      console.error('Error fetching recent sales:', error);
      throw new Error('Failed to fetch recent sales. Please check your connection and ensure the backend API is running.');
    }
  }

  async getLowStockProducts(limit: number = 8): Promise<LowStockProduct[]> {
    try {
      const response = await apiClient.get<ApiResponse<LowStockProduct[]>>(
        `${this.baseUrl}/low-stock?limit=${limit}`
      );

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch low stock products');
    } catch (error) {
      console.error('Error fetching low stock products:', error);
      throw new Error('Failed to fetch low stock products. Please check your connection and ensure the backend API is running.');
    }
  }

  async refreshDashboard(): Promise<void> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(`${this.baseUrl}/refresh`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to refresh dashboard');
      }
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      throw new Error('Failed to refresh dashboard. Please check your connection and ensure the backend API is running.');
    }
  }
}

export const dashboardService = new DashboardService();